import BaseMapWidgetContainer from 'components/BaseMap/BaseMapWidgetContainer';
import LegacyWidgetLayoutAdapter from 'components/Widgets/LegacyWidgetLayoutAdapter';

// Shared constants so widgetMeta stays consistent and easy to update.
// Default access list used by most widgets; override in widgetMeta when needed.
export const ACCESS_LEVELS_ALL = [8, 9, 10, 11, 12, 13];
export const SIZES_ALL = { '1x1': true, '1x2': true, '2x1': true };
export const SIZES_WIDE = { '1x1': false, '1x2': false, '2x1': true };
export const SIZES_GRAPH = { '1x1': false, '2x1': true };
export const SIZES_WIDE_TALL = { '1x1': false, '2x1': true, '1x2': true };
export const SIZES_TALL = { '1x1': false, '2x1': false, '1x2': true };
export const SIZES_TALL_WIDE = { '1x2': true, '2x1': true };
export const SIZES_WIDE_ONLY = { '2x1': true };

// Helpers used by widgetMeta to keep repeated JSX blocks out of the data.
// UI helpers used to keep widgetMeta entries declarative.
export const iconImage = (src) => () => <img src={src} style={{ width: 30 }} />;
export const sidebarDataFor = (href) => ({ href, accessLevel: ACCESS_LEVELS_ALL });
export const fullscreenOf = (Component) => () => <Component />;

// Baseline widget shape for metadata entries.
// Base factory to enforce consistent fields across widget metadata.
export const makeWidget = (id, base) => ({
  id,
  isResizable: false,
  ...base,
});

// Factory for category-level widget meta (Weather/Charts/Overwatch/etc).
// Factory for category-level widget meta; keeps repeated category/permission
// decisions out of widgetMeta.
export const createWidgetMetaHelpers = ({
  Charts,
  HourlyForecast,
  MarineForecast,
  Maps,
  Integrations,
  icons,
}) => {
  const {
    chartsIcon,
    forecastIcon,
    stormCenterIcon,
    marineForecastIcon,
    fullMapIcon,
    specialReportsIcon,
    connectedDevicesIcon,
    publicAlertsIcon,
    createAlertIcon,
  } = icons;

  // Forecast widgets share permissions, category, icon, and fullscreen target.
  const makeForecastWidget = (id, name, displayName, previewImages, href) =>
    makeWidget(id, {
      name,
      displayName,
      contentType: 'map',
      sizes: SIZES_ALL,
      previewImages,
      fullscreen: fullscreenOf(HourlyForecast),
      sidebarData: sidebarDataFor(href),
      category: ['Weather', 'Forecast'],
      icon: iconImage(forecastIcon),
      permissions: ['WeatherEnabled', 'ForecastEnabled'],
    });

  // Storm Center widgets share permissions, category, icon, and fullscreen target.
  const makeStormCenterWidget = (id, name, displayName, previewImages, href) =>
    makeWidget(id, {
      name,
      displayName,
      sizes: SIZES_ALL,
      previewImages,
      fullscreen: fullscreenOf(HourlyForecast),
      sidebarData: sidebarDataFor(href),
      category: ['Weather', 'Storm Center'],
      icon: iconImage(stormCenterIcon),
      permissions: ['WeatherEnabled', 'StormCenterEnabled'],
    });

  // Marine Forecast widgets share permissions, category, icon, and fullscreen target.
  const makeMarineForecastWidget = (
    id,
    name,
    displayName,
    previewImages,
    href
  ) =>
    makeWidget(id, {
      name,
      displayName,
      sizes: SIZES_ALL,
      previewImages,
      fullscreen: fullscreenOf(MarineForecast),
      sidebarData: sidebarDataFor(href),
      category: ['Weather', 'Marine Forecast'],
      icon: iconImage(marineForecastIcon),
      permissions: ['WeatherEnabled', 'MarineForecastEnabled'],
    });

  // Maps widgets use the Maps category; fullscreen target can be overridden.
  const makeMapsWidget = (
    id,
    name,
    displayName,
    previewImages,
    href,
    fullscreenComponent = Maps
  ) =>
    makeWidget(id, {
      name,
      displayName,
      sizes: SIZES_ALL,
      previewImages,
      fullscreen: fullscreenOf(fullscreenComponent),
      sidebarData: sidebarDataFor(href),
      category: ['Weather', 'Maps'],
      icon: iconImage(fullMapIcon),
      permissions: ['WeatherEnabled', 'MapsEnabled'],
    });

  // Chart widgets all point to the Charts page with specific tabs.
  const makeChartWidget = (
    id,
    name,
    displayName,
    preview1x1,
    preview2x1,
    href
  ) => ({
    id,
    name,
    displayName,
    isResizable: false,
    sizes: SIZES_WIDE,
    previewImages: {
      '1x1': preview1x1,
      '2x1': preview2x1,
    },
    fullscreen: fullscreenOf(Charts),
    sidebarData: sidebarDataFor(href),
    category: ['Weather', 'Charts'],
    icon: iconImage(chartsIcon),
    permissions: ['WeatherEnabled', 'ChartsEnabled'],
  });

  // Special Report widgets all point to Weather/Special Reports and tab.
  const makeSpecialReportWidget = (
    id,
    name,
    displayName,
    preview1x1,
    preview1x2,
    preview2x1,
    href,
    fullscreenComponent,
  ) => ({
    id,
    name,
    displayName,
    isResizable: false,
    sizes: SIZES_ALL,
    previewImages: {
      '1x1': preview1x1,
      '1x2': preview1x2,
      '2x1': preview2x1,
    },
    fullscreen: fullscreenOf(fullscreenComponent),
    sidebarData: sidebarDataFor(href),
    category: ['Weather', 'Special Reports'],
    icon: iconImage(specialReportsIcon),
    permissions: ['WeatherEnabled', 'SpecialReportsEnabled'],
  });

  // Overwatch/Connected Devices widgets share category and icon, but vary in
  // sizes, preview images, and permissions.
  const makeConnectedDevicesWidgetMeta = (
    id,
    name,
    displayName,
    sizes,
    previewImages,
    permissions,
    href = '/system/devices',
    fullscreenComponent = Integrations,
    contentType,
  ) =>
    makeWidget(id, {
      name,
      displayName,
      ...(contentType ? { contentType } : {}),
      sizes,
      previewImages,
      fullscreen: fullscreenOf(fullscreenComponent),
      sidebarData: sidebarDataFor(href),
      category: ['Overwatch', 'Connected Devices'],
      icon: iconImage(connectedDevicesIcon),
      permissions,
    });

  // Public Info widgets share category and icon selection; displayName also
  // doubles as the secondary category label.
  const makePublicInfoWidget = (
    id,
    name,
    displayName,
    sizes,
    previewImages,
    href,
    permissions,
    fullscreenComponent
  ) =>
    makeWidget(id, {
      name,
      displayName,
      sizes,
      previewImages,
      fullscreen: fullscreenOf(fullscreenComponent),
      sidebarData: sidebarDataFor(href),
      category: ['Public Info', displayName],
      icon: iconImage(id === 101 ? publicAlertsIcon : createAlertIcon),
      permissions,
    });

  return {
    makeForecastWidget,
    makeStormCenterWidget,
    makeMarineForecastWidget,
    makeMapsWidget,
    makeChartWidget,
    makeSpecialReportWidget,
    makeConnectedDevicesWidgetMeta,
    makePublicInfoWidget,
  };
};

// Factory for renderer helpers used by widgetRenderers.
// Factory for widget renderers; binds shared props once so renderers are concise.
export const createWidgetRendererHelpers = ({
  widgetRefresh,
  setWidgetRefresh,
  sidebarOpen,
}) => {
  const baseProps = { widgetRefresh, setWidgetRefresh, sidebarOpen };
  const noop = () => {};
  // Base renderer for widgets that accept dimensions/preview plus shared props.
  const renderWidget =
    (Component, extraProps = {}, adapterOptions = {}) =>
    (w, h, preview, widgetRuntime) => (
      <LegacyWidgetLayoutAdapter dimensions={[w, h]} {...adapterOptions}>
        {({ containerSizePx }) => (
          <Component
            {...baseProps}
            dimensions={[w, h]}
            preview={preview}
            containerSizePx={containerSizePx}
            widgetRuntime={widgetRuntime}
            {...extraProps}
          />
        )}
      </LegacyWidgetLayoutAdapter>
    );
  // Map widgets share one measured, overflow-safe container for every grid span.
  const renderMapWidget =
    (Component, extraProps = {}) =>
    (w, h, preview, widgetRuntime) => (
      <BaseMapWidgetContainer dimensions={[w, h]}>
        {({ dimensions, containerSizePx }) => (
          <Component
            {...baseProps}
            dimensions={dimensions}
            preview={preview}
            containerSizePx={containerSizePx}
            widgetRuntime={widgetRuntime}
            {...extraProps}
          />
        )}
      </BaseMapWidgetContainer>
    );
  // ConnectedDevices widgets require a specific widgetType and stubbed setters.
  const renderConnectedDevices =
    (widgetType, ConnectedDevicesWidget, { mapWidget = false } = {}) =>
    (w, h, preview, widgetRuntime) => {
      const content = (dimensions) => (
        <ConnectedDevicesWidget
          widgetType={widgetType}
          dimensions={dimensions}
          widget
          setShowCurrentDevice={noop}
          setShowDeviceSettings={noop}
          selectedGroup={null}
          setSelectedGroup={noop}
          widgetRuntime={widgetRuntime}
        />
      );

      return mapWidget ? (
        <BaseMapWidgetContainer dimensions={[w, h]}>
          {({ dimensions }) => content(dimensions)}
        </BaseMapWidgetContainer>
      ) : (
        content([w, h])
      );
    };
  return {
    renderWidget,
    renderMapWidget,
    renderConnectedDevices,
  };
};
